import styled from 'styled-components';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { RowView, Text } from '../../helpers';
import RoundBox from '../RoundBox/RoundBox';
import { colors } from '../../assets/colors';
import { IInvoice } from '../../interfaces';
import { dateTransform } from '../../functions';
import { useTranslation } from 'react-i18next';

type AttachmentDetailsProps = {
    invoice: IInvoice,
    clickable?: boolean
}

const AttachmentList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 15px;
    list-style: none;
    margin-top: 15px;
`;

const AttachmentListItem = styled.li`
    padding: 12px 16px;
    margin-bottom: 8px;
    background: #fefefe;
    width: 100%;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s, box-shadow 0.2s;
    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    }
    &:active {
        transform: translateY(0px);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    @media screen and (max-width: 400px) {
        padding: 4px 6px;
    }
`;

const IconTextWrapper = styled.div`
    display: flex;
    width: calc(100% - 32px);
    align-items: center;
    gap: 10px;
    max-width: 85%;
`;

const StyledLink = styled.a`
    display: flex;
    width: 100%;
    justify-content: space-between;
    text-decoration: none;
`;
const StyledSpan = styled.span`
    display: flex;
    width: 100%;
    justify-content: space-between;
    text-decoration: none;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
`;

const RightSide = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const AttachmentsDetails = ({ invoice, clickable = true }: AttachmentDetailsProps) => {
    const { t } = useTranslation();
    const isPartlySigned = invoice.attachments?.some(({ stage }) => stage === 'PARTLY_SIGNED');

    const handleDownload = (fileUrl: string, fileName: string) => {
        try {
            // Since backend sends Content-Disposition: attachment, use simple approach
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = fileName;
            link.style.display = 'none';

            // Trigger download without opening new tab
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            // Fallback: open in new tab if download fails
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const renderStatusIcon = (stage?: string) => {
        if (stage === 'SIGNED') {
            return <CheckCircleIcon sx={{ color: colors.green }} />;
        }
        if (stage === 'PARTLY_SIGNED') {
            return <HourglassBottomIcon sx={{ color: colors.blue }} />;
        }
        return null;
    };

    if (!invoice.attachments || invoice.attachments.length === 0) {
        return null;
    } else {
        return (
            <RoundBox>
                <Text i={t('downloadAttachmentsTitle')} fontWeight="bold" header />
                {isPartlySigned && (
                    <Text
                        i={t('attachmentsPartlySignedMessage')}
                        color={colors.lightGrey}
                    />
                )}
                <AttachmentList>
                    {invoice.attachments.map(({ file_name, uploaded_at, file_url, stage }, index) => (
                        <AttachmentListItem key={`${file_name + index}`}>
                            <RowView>
                                {clickable ? (
                                    <StyledLink
                                        href={file_url}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        <IconTextWrapper>
                                            <PictureAsPdfIcon sx={{ color: colors.main }} />
                                            <Text i={file_name} />
                                        </IconTextWrapper>
                                        <RightSide>
                                            <Text i={`${dateTransform(uploaded_at, invoice)}`} />
                                            {renderStatusIcon(stage)}
                                        </RightSide>
                                    </StyledLink>
                                ) : (
                                    <StyledSpan
                                        onClick={() => handleDownload(file_url, file_name)}>
                                        <IconTextWrapper>
                                            <PictureAsPdfIcon sx={{ color: colors.main }} />
                                            <div style={{ maxWidth: `calc(100% - 50px)` }}>
                                                <Text
                                                    i={file_name}
                                                    style={{
                                                        overflow: 'hidden',
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                />
                                            </div>

                                        </IconTextWrapper>
                                        <RightSide>
                                            <Text i={`${dateTransform(uploaded_at, invoice)}`} />
                                            {renderStatusIcon(stage)}
                                        </RightSide>
                                    </StyledSpan>
                                )}
                            </RowView>
                        </AttachmentListItem>
                    ))}
                </AttachmentList>
            </RoundBox>
        );
    }
};

export default AttachmentsDetails;
